import React, { useState } from 'react';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { groupSectionsByCategory } from 'components/Reports/sitrepSectionGrouping';
export { sectionsHaveCategories } from 'components/Reports/sitrepSectionGrouping';

type SitrepSectionTabsProps = {
  sections: SitrepSectionType[];
  /**
   * Use activeSectionIndex from useSitrepSectionSelector for correct active
   * highlighting. Only drives the tab bar when sections have no categories —
   * categorized sections are all shown at once (see `renderSection`).
   */
  activeSectionIndex: number | null;
  onSectionChange: (index: number) => void;
  /**
   * Renders a section's body as a card. Required when sections belong to
   * template categories: the tab bar switches between categories, and every
   * section in the active category is rendered below it via this callback.
   * Unused for uncategorized sections — that single-active-section body is left
   * to the caller, as before.
   */
  renderSection?: (
    section: SitrepSectionType,
    index: number
  ) => React.ReactNode;
  className?: string;
  idBase?: string;
};

/**
 * Renders tab buttons for navigating sitrep sections. When sections belong to
 * template categories they are rendered as labelled groups, otherwise as a
 * single horizontal row. AI-generated sections are indicated with a sparkle
 * icon. Returns null when there are no sections.
 */
const SitrepSectionTabs: React.FC<SitrepSectionTabsProps> = ({
  sections,
  activeSectionIndex,
  onSectionChange,
  renderSection,
  className,
  idBase,
}) => {
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);

  if (sections.length === 0) return null;

  const selectedIndex = activeSectionIndex ?? 0;

  const groups = groupSectionsByCategory(sections);
  const hasCategories = groups.some((group) => group.category !== null);

  if (!hasCategories) {
    return (
      <Tabs
        value={selectedIndex}
        onValueChange={onSectionChange}
        className="tw:w-full tw:min-w-0"
      >
        <TabsList
          className={`tw:w-full tw:min-w-0 tw:justify-start d-flex tw:overflow-x-auto tw:overflow-y-hidden flex-nowrap p-1 ${className ? ` ${className}` : ''}`}
          aria-label="SITREP sections"
          activateOnFocus
        >
          {sections.map((section, index) => (
            <TabsTrigger
              key={section.id}
              value={index}
              className="tw:flex-none"
              id={idBase ? `${idBase}-tab-${index}` : undefined}
              aria-controls={idBase ? `${idBase}-panel-${index}` : undefined}
            >
              <span className="d-flex align-items-center gap-1">
                {section.name}
                {section.type === 'ai' && (
                  <SharedIcon
                    iconName="auto_awesome"
                    size="sm"
                    aria-hidden="true"
                  />
                )}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    );
  }

  // Categorized sections: the tab bar switches between categories (styled
  // identically to the flat section tabs above), and every section in the
  // active category renders as a card in the body below.
  const selectedCategoryIndex = Math.min(
    activeCategoryIndex,
    groups.length - 1
  );
  const activeGroup = groups[selectedCategoryIndex];

  return (
    <div
      className={`d-flex flex-column tw:min-w-0 w-100 gap-2 p-1 ${className ? ` ${className}` : ''}`}
    >
      <Tabs
        value={selectedCategoryIndex}
        onValueChange={setActiveCategoryIndex}
        className="tw:w-full tw:min-w-0"
      >
        <TabsList
          className="tw:w-full tw:min-w-0 tw:justify-start d-flex tw:overflow-x-auto tw:overflow-y-hidden flex-nowrap"
          aria-label="SITREP categories"
          activateOnFocus
        >
          {groups.map((group, index) => (
            <TabsTrigger
              key={group.category ?? `uncategorized-${index}`}
              value={index}
              className="tw:flex-none"
              id={idBase ? `${idBase}-category-tab-${index}` : undefined}
              aria-controls={idBase ? `${idBase}-category-panel` : undefined}
            >
              {group.category ?? 'Other'}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div
        id={idBase ? `${idBase}-category-panel` : undefined}
        role="tabpanel"
        aria-labelledby={
          idBase ? `${idBase}-category-tab-${selectedCategoryIndex}` : undefined
        }
        className="d-flex flex-column gap-3"
      >
        {activeGroup.sections.map(({ section, index }) => (
          <div key={section.id} className="reports-card__section-content">
            {renderSection?.(section, index)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SitrepSectionTabs;
